// DownloadPage.jsx — Served at /download route in the React SPA
import { useState, useEffect } from "react";
import { SERVER } from "./api";

const C = {
  bg: "#080A0E", card: "#111318", border: "#1A1D24",
  accent: "#00D4AA", text: "#CBD5E1", muted: "#4B5563"
};

function fmt_bytes(b) {
  if (!b) return "";
  if (b > 1e6) return `${(b / 1e6).toFixed(1)} MB`;
  return `${(b / 1e3).toFixed(0)} KB`;
}

export default function DownloadPage() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${SERVER}/download/info`)
      .then(r => r.json())
      .then(d => { setInfo(d); setLoading(false); })
      .catch(() => { setInfo({ available: false }); setLoading(false); });
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', system-ui, sans-serif", color: C.text, padding: 24 }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');`}</style>

      {/* Grid bg */}
      <div style={{ position: "fixed", inset: 0, backgroundImage: `linear-gradient(${C.border}55 1px, transparent 1px), linear-gradient(90deg, ${C.border}55 1px, transparent 1px)`, backgroundSize: "40px 40px", pointerEvents: "none" }}/>
      <div style={{ position: "fixed", inset: 0, background: `radial-gradient(ellipse at 50% 0%, ${C.accent}0D, transparent 55%)`, pointerEvents: "none" }}/>

      <div style={{ position: "relative", textAlign: "center", maxWidth: 540, width: "100%" }}>
        {/* Logo */}
        <img
          src="/logo512.png"
          alt="Tavern"
          style={{ width: 96, height: 96, borderRadius: 24, margin: "0 auto 20px", display: "block", boxShadow: `0 12px 40px ${C.accent}44` }}
        />

        <h1 style={{ fontSize: 36, fontWeight: 800, color: C.text, margin: "0 0 8px", letterSpacing: -1 }}>
          Tavern
        </h1>
        <p style={{ color: C.muted, fontSize: 16, margin: "0 0 40px" }}>
          Your self-hosted community. Download the desktop app for the full experience.
        </p>

        {/* Main download card */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: "32px 36px", marginBottom: 20, textAlign: "left" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
            <img src="/logo192.png" alt="Tavern" style={{ width: 52, height: 52, borderRadius: 14, objectFit: "cover" }}/>
            <div>
              <div style={{ fontWeight: 700, fontSize: 18, color: C.text }}>Windows Desktop App</div>
              <div style={{ color: C.muted, fontSize: 13, marginTop: 2 }}>
                Windows 10 / 11 · x64
                {info?.size ? ` · ${fmt_bytes(info.size)}` : ""}
                {info?.updated ? ` · Updated ${new Date(info.updated).toLocaleDateString()}` : ""}
              </div>
            </div>
          </div>

          {/* Feature list */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px", marginBottom: 28 }}>
            {[
              "Native OS notifications",
              "Taskbar unread badge",
              "Minimize to system tray",
              "Camera & mic (no prompt)",
              "Auto-updates silently",
              "Custom dark titlebar",
            ].map(f => (
              <div key={f} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.text }}>
                <span style={{ color: C.accent, fontWeight: 700 }}>✓</span> {f}
              </div>
            ))}
          </div>

          {loading ? (
            <div style={{ width: "100%", padding: "14px 0", borderRadius: 12, background: "#1A2A2A", color: C.muted, fontSize: 15, fontWeight: 600, textAlign: "center" }}>
              Checking availability...
            </div>
          ) : info?.available ? (
            <a href={`${SERVER}/download/windows`} style={{
              display: "block", width: "100%", padding: "15px 0", borderRadius: 12, textAlign: "center",
              background: `linear-gradient(90deg, #00B896, ${C.accent})`,
              color: "#0A0C10", fontSize: 16, fontWeight: 800, textDecoration: "none",
              boxShadow: `0 6px 24px ${C.accent}44`, letterSpacing: 0.3,
            }}>
              ⬇ Download for Windows
            </a>
          ) : (
            <div style={{ width: "100%", padding: "14px 0", borderRadius: 12, background: "#1A1D24", border: `1px solid ${C.border}`, color: C.muted, fontSize: 14, fontWeight: 600, textAlign: "center" }}>
              Installer not yet available — use the web app below
            </div>
          )}
        </div>

        {/* Web app fallback */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: "20px 28px", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ fontSize: 28 }}>🌐</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>Use in Browser</div>
              <div style={{ color: C.muted, fontSize: 12 }}>No install needed · Works on any device</div>
            </div>
          </div>
          <a href="/" style={{ background: "#1A1D24", border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, fontSize: 13, fontWeight: 700, padding: "9px 18px", textDecoration: "none" }}>
            Open App →
          </a>
        </div>

        {/* Mac / Linux note */}
        <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.6 }}>
          Mac and Linux builds available on request. <br/>
          The web app works on all platforms without any install.
        </div>

        <div style={{ marginTop: 32, color: "#2A2D36", fontSize: 11 }}>
          tavern.d20madjd.quest · Self-hosted
        </div>
      </div>
    </div>
  );
}
